"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader } from "lucide-react";
const metadataRows = [{
  label: "Response time",
  value: "2 business days"
}, {
  label: "Discovery call",
  value: "90 minutes"
}, {
  label: "Audit proposal",
  value: "Within 2 weeks of call"
}];
const pillars = [{
  id: "crm_pipelines",
  label: "CRM Communication Pipelines"
}, {
  id: "fsm_tracking",
  label: "FSM Operation Tracking"
}, {
  id: "automated_bookkeeping",
  label: "Automated Bookkeeping"
}];
const stages = ["Startup", "Scale-up", "Enterprise", "Public Sector", "Cultural"];
function EngagementForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8">
        <p className="text-base text-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 h-12 px-6 border border-foreground bg-transparent text-foreground hover:bg-transparent hover:text-primary hover:border-primary">
          Submit another brief
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Your Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="h-12 border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="h-12 border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" rules={{
      required: "Organization is required"
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Company / Organization
            </FormLabel>
            <FormControl>
              <Input placeholder="Acme Corp" {...field} className="h-12 border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="role" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Your Role
            </FormLabel>
            <FormControl>
              <Input placeholder="Owner / Manager" {...field} className="h-12 border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[\d\s\-()]+$/,
        message: "Enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-123-4567" {...field} className="h-12 border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground" />
            </FormControl>
            <FormMessage />
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox className="mt-1 size-4 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-white" />
                <span className="text-[0.7rem] text-muted-foreground leading-relaxed">
                  By providing your phone number and checking this box, you agree to receive automated CRM updates, account notifications, and marketing text messages from CSOS. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt-out. See Terms & Privacy.
                </span>
              </label>
            </div>
          </FormItem>} />

      <FormField control={control} name="services" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Services of Interest
            </FormLabel>
            <FormControl>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-12 w-full border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground data-[placeholder]:text-muted-foreground">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="border border-border">
                  {stages.map((stage, i) => <SelectItem key={stage} data-index={i} value={stage} className="">
                      {stage}
                    </SelectItem>)}
                  <SelectItem value="CRM Setup" className="">CRM Setup</SelectItem>
                  <SelectItem value="FSM Integration" className="">FSM Integration</SelectItem>
                  <SelectItem value="Bookkeeping" className="">Bookkeeping</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="pillars" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Pillars of Interest
            </FormLabel>
            <div className="flex flex-col gap-3 pt-2">
              {pillars.map((pillar, i) => {
          const current: string[] = Array.isArray(field.value) ? field.value : [];
          const checked = current.includes(pillar.id);
          return <label key={pillar.id} data-index={i} className="flex items-center gap-3 cursor-pointer text-sm text-foreground font-default">
                    <Checkbox checked={checked} onCheckedChange={value => {
              if (value === true) {
                field.onChange([...current, pillar.id]);
              } else {
                field.onChange(current.filter(v => v !== pillar.id));
              }
            }} className="size-4 border-border data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background" />
                    <span>{pillar.label}</span>
                  </label>;
        })}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please describe your project",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-muted-foreground font-default">
              Project Details / Message
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Describe your current operational challenges..." {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-primary p-4">
          <p className="text-sm text-primary font-default">{submitError}</p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-6">
        <Button type="submit" disabled={isSubmitting} className="h-12 bg-primary text-white hover:bg-primary/90 px-8 text-sm font-medium tracking-[0.02em] sm:min-w-[280px]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {"Sending..."}
            </> : "Submit brief"}
        </Button>
        <span className="text-xs tracking-[0.02em] text-muted-foreground font-default">
          We respond personally; no automated reply.
        </span>
      </div>
    </Form>;
}
export default function Foundation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="19552" id="build-your-foundation" className="bg-background py-32 md:py-44">
      <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-16 transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          {}
          <div className="md:col-span-5">
            <h2 className="font-default font-medium text-foreground text-[clamp(2.25rem,4.2vw,4rem)] leading-[1.02] tracking-[-0.025em]">
              Build your foundation.
            </h2>

            <p className="mt-8 max-w-md text-base leading-[1.6] text-muted-foreground font-default">
              Share the shape of your operational challenge. We respond within two business days with a tailored discovery call to architect your infrastructure. No templates, no automated funnels.
            </p>

            <ul className="mt-14 border-t border-border">
              {metadataRows.map((row, i) => <li key={row.label} data-index={i} className="flex items-baseline justify-between gap-6 border-b border-border py-4">
                  <span className="text-xs tracking-[0.02em] text-muted-foreground font-default">
                    {row.label}
                  </span>
                  <span className="text-xs tracking-[0.02em] text-foreground font-default text-right">
                    {row.value}
                  </span>
                </li>)}
            </ul>
          </div>

          {}
          <div className="hidden md:block md:col-span-1" />

          {}
          <div className="md:col-span-6">
            <FormProvider formKey="lead_form_home" formId="lead_form_home" sectionName="build-your-foundation" defaultValues={{
            name: "",
            email: "",
            company: "",
            role: "",
            phone: "",
            services: "",
            pillars: [],
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            company: {
              required: true
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit brief" successMessage="Brief received. A principal will reach out within two business days." errorMessage="We couldn't submit your brief. Please try again or email info@csos.com.">
              <EngagementForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}